import { createContext, useContext } from 'react'

import type { ApiError } from './api-error'
import type { AuthMode } from './config'

/** Der angemeldete Nutzer. Rollen kommen aus dem Identity-Dienst, nicht aus dem Token. */
export interface User {
  id: string
  name: string
  email: string
  roles: string[]
}

export interface AccessTokenOptions {
  /** Überspringt den MSAL-Cache und erneuert das Token beim Aussteller. */
  forceRefresh?: boolean
}

export interface AuthContextValue {
  /** Der angemeldete Nutzer oder null, solange niemand angemeldet ist. */
  user: User | null
  isAuthenticated: boolean
  /**
   * true, solange Anmeldung oder Rollen noch geladen werden — einschließlich
   * der Zeit, in der noch niemand angemeldet ist und die Umleitung zur
   * Anmeldung läuft.
   *
   * Wichtig für Rollen-Guards: `user === null` bei `isLoading === false`
   * heißt „die Anmeldung ist gescheitert", nicht „keine Berechtigung".
   */
  isLoading: boolean
  /** Gesetzt, wenn die Anmeldung fehlgeschlagen ist. */
  error: Error | null
  /** Welcher Modus aktiv ist. Nur für Diagnose — Modulcode darf das nicht auswerten. */
  mode: AuthMode
  login: () => void
  logout: () => void
  /** Wiederholt das Laden des zentralen Anmelde- und Rollenstatus. */
  retryAuthentication: () => void
  /** Holt ein Zugriffstoken für die angegebenen Scopes. Im Mock-Modus null. */
  getAccessToken: (
    scopes: string[],
    options?: AccessTokenOptions,
  ) => Promise<string | null>
  /** Meldet 401/403 aus einem API-Aufruf an die Shell. */
  reportApiAuthError: (error: ApiError) => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Zugriff auf den Anmeldezustand. In Modulen braucht man meist nur
 * {@link useUser} oder {@link useHasRole}.
 *
 * @example
 * ```tsx
 * const { user, logout } = useAuth()
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error(
      'useAuth() wurde außerhalb von <AuthProvider> verwendet. ' +
        'Der Provider wird von der Shell gesetzt — in einem Modul ist das ein Zeichen dafür, ' +
        'dass die Komponente außerhalb der Modul-Routes gerendert wird.',
    )
  }
  return context
}

/**
 * Der angemeldete Nutzer. Innerhalb der Modul-Routes ist immer jemand
 * angemeldet — die Shell rendert Module sonst gar nicht erst.
 *
 * @example
 * ```tsx
 * const user = useUser()
 * <p>Angemeldet als {user.name}</p>
 * ```
 */
export function useUser(): User {
  const { user } = useAuth()
  if (!user) {
    throw new Error(
      'useUser() wurde aufgerufen, obwohl niemand angemeldet ist. ' +
        'Innerhalb eines Moduls kann das nicht passieren; außerhalb useAuth() verwenden.',
    )
  }
  return user
}

/** Wildcard-Rolle des Mock-Modus: passt auf jede Rollenabfrage. */
export const ALL_ROLES = '*'

/**
 * Prüft eine einzelne Rolle.
 *
 * @example
 * ```tsx
 * const darfFreigeben = useHasRole('wartung.freigabe')
 * {darfFreigeben && <Button>Freigeben</Button>}
 * ```
 */
export function useHasRole(role: string): boolean {
  const { user } = useAuth()
  return hasAnyRole(user?.roles, [role])
}

/**
 * Prüft, ob der Nutzer mindestens eine der Rollen hat.
 * Ein leeres Array bedeutet „für alle sichtbar" und ergibt true.
 *
 * @example
 * ```tsx
 * const sichtbar = useHasAnyRole(modul.roles)
 * ```
 */
export function useHasAnyRole(roles: string[]): boolean {
  const { user } = useAuth()
  return hasAnyRole(user?.roles, roles)
}

/**
 * Rollenprüfung ohne Hook — für Filter außerhalb von Komponenten.
 *
 * @example
 * ```ts
 * const sichtbareModule = modules.filter((m) => hasAnyRole(user.roles, m.roles))
 * ```
 */
export function hasAnyRole(
  userRoles: string[] | undefined,
  requiredRoles: string[],
): boolean {
  if (requiredRoles.length === 0) return true
  if (!userRoles) return false
  if (userRoles.includes(ALL_ROLES)) return true
  return requiredRoles.some((role) => userRoles.includes(role))
}
